﻿import React, { useRef } from "react";

/**
 * This is for the worse case scenarion - IE and
 * input has validations but register was not supplied
 * with a validationHandler to display a custom error
 * message!
 * This isn't great but it is still much better then no
 * feedback whatsoever.
 */
if (!HTMLInputElement.prototype.reportValidity) {
    HTMLInputElement.prototype.reportValidity =
        function reportValidity(): boolean {
            const ele = this as HTMLInputElement;

            if (ele.validationMessage.length) {
                // eslint-disable-next-line no-alert
                alert(`${ele.name}: ${ele.validationMessage}`);
                return false;
            }
            return true;
        };
}

/**
 * Validation state and helpers for when a form field fails
 * validation.
 *
 * This object is passed into validationHandlers supplied when
 * registering a field for the useForm hook. See that documentation
 * for examples on it's use case.
 */
export interface IInvalidProps {
    /**
     * Represents the validity state that a field is in
     * after trying to submit - on of the states inside this
     * object signals why this field failed to validate.
     *
     * [Mozilla: ValidityState](https://developer.mozilla.org/en-US/docs/Web/API/ValidityState)
     * ### Compatibility Notes
     *
     * This is mostly widely supported across different browsers apart from
     * the two exceptions below for IE:
     *
     * - badInput: has no support, but has a limited and obscure use case.
     * - tooShort: has no support, use a regex pattern instead; then check for patternMismatch.
     */
    validityState: ValidityState;

    /**
     * Reference to the registered element that may be the invalid input or a
     * container housing the invalid input element. This allows for custom changes
     * to be made (if required) to the registered element.
     */
    ref: HTMLElement;

    /**
     * Helper function to set a CSS Class on the registered element and all children
     * elements for a given number of milliseconds.
     *
     * When using this helper with container elements make sure that the CSS
     * handles styling of different elements differently.
     *
     * @param className CSS error class to add to every element in the registered element.
     * @param ms amount of milliseconds to maintain the CSS class before removing it.
     */
    setErrorClass: (className: string, ms: number) => void;

    /**
     * Helper function to change the label element value with the given value for the given
     * number of milliseconds. When the number of milliseconds have lapsed then the label
     * will revert to it's original value.
     *
     * If the registered element does not have a label element then this helper will do
     * nothing - ensure the element contains a label if this is the only feedback you
     * are providing a user.
     *
     * @param value Label value to display for the given amount of time before reverting to previous.
     * @param ms amount of milliseconds to display the new label.
     */
    setLabel: (value: string, ms: number) => void;
}

/**
 * A handler for when a registered field does not validate.
 * The useForm hook will provide the ValidityState and the
 * registered field aswell as some helper functions.
 *
 * ValidationHandler should check for the validation state
 * and if it handles that state then return true otherwise
 * it should perform no action and return false.
 *
 *
 * ### Example
 * ```ts
 * // ValidationHandler to enforce `required` attribute
 * const mandatory = ({
 *          setLabel,
 *          setErrorClass,
 *          validityState
 *      }: IInvalidProps): boolean => {
 *      if (validityState.valueMissing) {
 *          setLabel(`This field is mandatory`, 3000);
 *          // Material-UI error class
 *          setErrorClass(`Mui-error`, 3000);
 *          return true;
 *      }
 *      return false;
 *  };
 *  ```
 */
export type ValidationHandler = (props: IInvalidProps) => boolean;

/**
 * Options to use when registering a new element to useForm hook.
 *
 * Currently only contains validationHandlers but could in the future
 * include filters to prevent incorrect user input as they type.
 *
 * ### Note
 * **If you define validation on a registered field then define a handler for it!**
 *
 * If no validationHandlers are provided and the input element can fail validation
 * OR none of them handle the validation then the useForm hook will default to
 * using the reportValidity option.
 * As IE has no support for reportValidity then this will default to using
 * an `alert` with the validationMessage (which is supported).
 * [Mozilla: reportValidity](https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormElement/reportValidity)
 */
export interface IRegisterProps {
    /**
     * A list of validation handlers to use when a registered field does
     * not validate when trying to submit the form.
     *
     * Validation handlers return a boolean to signal whether that handler
     * has handled the validation state this will prevent the following
     * handlers from firing.
     *
     * Validation handlers should be in the desired order of execution
     * as they will called in order until one signals they have handled
     * the validation state.
     */
    validationHandlers?: ValidationHandler[];
}

/**
 * Internal object which holds the registered element and the register
 * options.
 */
interface IInternalRegisterProps {
    /**
     * Reference to the registered element.
     */
    ref: HTMLElement;

    /**
     * Options provided when the element ref was registered.
     */
    registerOptions?: IRegisterProps;
}

/**
 * Handles form submissions and will check validation before creating the TForm object
 * and passing it to the given function.
 *
 * If any of the registered fields do not validate then the function given is not called
 * and instead the ValidationHandler's for each field are called respectively. This means
 * that provided all the properties of the object is registered in the form then the
 * onSubmit function will be passed a validated version of that object.
 *
 * ### Example
 *
 * ```ts
 * const onSubmit = (data: MyData) => {
 *  // Posts the data up to the server or something else useful
 * ...
 * };
 *
 * const {
 *      handleSubmit,
 *      register
 * } = useForm<MyData>();
 *
 * <form className={`form-me-up-scotty`}
 *      noValidate
 *      onSubmit={handleSubmit(onSubmit)}
 * ... // rest of the form - registering all properties of `MyData` object.
 *
 * ```
 *
 * ### Troubleshooting
 * If TForm is arriving in your custom onSubmit function incorrectly formed then
 * check that all fields have been correctly registered - if they are not registered
 * then useForm will ignore the input.
 *
 */
export type SubmitFormHandler<TForm> = (
    onSubmit: (values: TForm) => Promise<void>,
) => (e: React.ChangeEvent<HTMLFormElement>) => void;

/**
 * This hook provides functions to help handler validations and to collect the form
 * input, when valid, and pass this as an object for easy use inside the app or to
 * send back to the server.
 *
 * useForm interally does not use any React methods that cause a rerender of this
 * hook this means that validation changes or successful submit handling does not
 * cause the form to rerender and takes advantage of HTML5 native validation which
 * is mostly supported (notable exceptions mentioned where they apply).
 *
 * ### Note
 * When using this hook to help with validation provide the 'noValidate' attribute to the
 * form like such:
 * ```ts
 * const {
 *      handleSubmit,
 *      register,
 * } = useForm<GenericDataObject>
 *
 * <form className={`no-form-better`}
 *      noValidate // Not adding this will cause the form to validate natively
 *      onSubmit={handleSubmit(myCustomGenericDataObjectFunction)}
 * ...
 * ```
 */
export function useForm<TForm>(): {
    handleSubmit: SubmitFormHandler<TForm>;
    register: (props?: IRegisterProps) => (ref: HTMLElement | null) => void;
} {
    // Set to avoid duplications
    const registered = useRef<Set<IInternalRegisterProps>>(new Set());

    /** Register the field to the internal register */
    const registerField = (props: IInternalRegisterProps): void => {
        registered.current.add(props);
    };

    /** Registers non null HTMLElement and the respective register options */
    function register(
        props?: IRegisterProps,
    ): (ref: HTMLElement | null) => void {
        return (ref): void => {
            if (ref) {
                // Strict reference only
                registerField({
                    ref,
                    registerOptions: props,
                });
            }
        };
    }

    const injectAndFireInvalid = (
        reg: IInternalRegisterProps,
        input: HTMLInputElement | HTMLTextAreaElement,
    ): boolean => {
        // ESLint: optional-chain here doesn't parse correctly..
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (reg.registerOptions && reg.registerOptions.validationHandlers) {
            const invalid = reg.registerOptions.validationHandlers;

            return invalid.every(
                (check) =>
                    !check({
                        ref: reg.ref,
                        setErrorClass: createSetErrorClass(reg.ref),
                        setLabel: createSetLabel(reg.ref),
                        validityState: input.validity,
                    }),
            );
        } else {
            // Native validation fall through
            return input.reportValidity();
        }
    };

    /**
     * Checks if the current element is an input element or tries to find one in
     * the children of the current element.
     * @returns a HTMLInputElement if one was found, otherwise null.
     */
    const tryGetInput = (
        value: HTMLElement,
    ): HTMLInputElement | HTMLTextAreaElement | null =>
        value instanceof HTMLInputElement
            ? value
            : value.querySelector(`input`) || value.querySelector(`textarea`);

    /**
     * Handles onSubmit for HTMLFormElements which will try to validate the
     * registered fields before passing the validated data to the onSubmit
     * function given.
     *
     * @param onSubmit function to pass the validated values to.
     */
    const handleSubmit =
        (
            onSubmit: (values: TForm) => void,
        ): ((e: React.ChangeEvent<HTMLFormElement>) => void) =>
        (e): void => {
            e.preventDefault(); // Stop event bubbling

            const values: { [key: string]: string } = {};

            let errorFlag = false;

            registered.current.forEach((r) => {
                const input = tryGetInput(r.ref);

                if (input) {
                    if (!injectAndFireInvalid(r, input)) {
                        errorFlag = true;
                    } else {
                        values[input.name] = input.value;
                    }
                }
            });
            if (!errorFlag) {
                onSubmit(values as unknown as TForm);
            }
        };

    return {
        handleSubmit,
        register,
    };
}

/**
 * VALIDATION HELPER FUNCTIONS
 *
 * Future: could move these to a separate file and pass an object to useForm to add
 * more validation helpers? Provide a default interface/object including the below and
 * use that as a default if no other helpers are required.
 */

/**
 * Creates a helper function that will add a new CSS class to the reference element
 * for a given duration before removing.
 * @param ref reference to the HTMLElement that this helper will be used for.
 */
const createSetErrorClass =
    (ref: HTMLElement): ((className: string, ms: number) => void) =>
    (cn, delay): void => {
        const elements = [ref];

        // Ignore svg elements because of IE.
        const children = ref.querySelectorAll(`:not(svg)`);

        for (let i = 0; i < children.length; i++) {
            elements.push(children[i] as HTMLElement);
        }

        elements.forEach((e) => {
            if (e.classList) {
                e.classList.add(cn);
            }
        });

        setTimeout(() => {
            elements.forEach((e) => {
                if (e.classList) {
                    e.classList.remove(cn);
                }
            });
        }, delay);
    };

/**
 * Creates a helper function that set a new label for a given duration before
 * reverting to the previous label value.
 * @param ref reference to the HTMLElement that this helper will be used for.
 */
const createSetLabel =
    (ref: HTMLElement): ((label: string, ms: number) => void) =>
    (value, delay): void => {
        const labelElement = ref.querySelector(`label`);

        if (labelElement) {
            const curr = labelElement.innerHTML;

            if (curr === value) {
                // Short circuit if this has already occurred.
                return;
            }

            labelElement.innerHTML = value;

            setTimeout(() => {
                labelElement.innerHTML = curr;
            }, delay);
        }
    };
